import { Box } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { QuizContent } from '@/clients/utoniq-core/schema'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type UserAnswerFormProps = {
  content: QuizContent
  isAnswered: boolean
  noAnswer: boolean
  onSubmitAnswer: (values: AnswerFormValues) => Promise<void>
}

export type AnswerFormValues = {
  answer: string
}

const defaultValues = {
  answer: '',
}

export const UserAnswerForm: Organism<UserAnswerFormProps> = ({
  onSubmitAnswer,
  isAnswered,
  content,
  noAnswer,
}) => {
  const { showError } = useMessage()

  const schema: UseFormProps['schema'] = o => ({
    answer: noAnswer
      ? o.any().allow(null).optional()
      : o.string().label(tLabel('missionDetail.answer')).max(50),
  })

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm<AnswerFormValues>({
    schema,
    defaultValues,
  })

  const onSubmit = handleSubmit(values =>
    onSubmitAnswer(values).catch(error =>
      handleError<AnswerFormValues>(error, setError, showError)
    )
  )

  return (
    <Form id="user-answer-form" onSubmit={onSubmit} sx={{ bgcolor: 'none' }}>
      {!noAnswer && (
        <>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              img: {
                width: '100%',
                display: 'block',
              },
            }}
          >
            <img src={content.image?.uri ?? ''} alt="" />
          </Box>

          {!isAnswered && (
            <TextInputCell
              type="text"
              error={errors?.answer}
              {...register('answer')}
            />
          )}
        </>
      )}
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('missionDetail')
    return (
      <CellFrame>
        <TextInputField
          ref={ref}
          t={t}
          textFieldProps={{
            sx: {
              bgcolor: 'inherit',
            },
            InputProps: {
              sx: {
                bgcolor: 'grey.100',
              },
            },
            FormHelperTextProps: {
              sx: {
                bgcolor: 'inherit',
              },
            },
          }}
          {...props}
        />
      </CellFrame>
    )
  }
)
